/**
 * Request/response schemas and the appointment status state machine for the scheduling surface
 * (`docs/roadmap.md` Phase 4, `emr-po` A8: core scheduling only).
 *
 * Same reasoning as `authRequests.ts` for living in `shared/` rather than beside the routes: the
 * server validates against these at the boundary and a future calendar UI infers its own types from
 * the same objects, so the two cannot drift. Every object schema is strict for the same mass-
 * assignment reason `authRequests.ts` states — nothing here is spread into an insert today, and the
 * schema still refuses an unrecognised key, because "nothing spreads it today" is not a property a
 * future edit preserves.
 *
 * No server-owned value appears in any request schema: no `orgId` (the session's tenant, never the
 * caller's), no `status` on create (every appointment starts `scheduled`), no timestamps.
 *
 * **The status transition table is a business rule, not routing logic** — `claude-kit/rules/
 * invariants.md`'s "Business rules live in pure, tested modules" — so it lives here, covered by
 * `schedulingRequests.test.ts` with no database, and the server calls `canTransitionAppointmentStatus`
 * rather than re-deriving which transitions are legal.
 */

import { z } from 'zod';

import { RESTRICTION_REASONS } from './confidentiality.ts';

export const MIN_APPOINTMENT_TYPE_LENGTH = 1;
export const MAX_APPOINTMENT_TYPE_LENGTH = 200;
export const MAX_APPOINTMENT_NOTES_LENGTH = 4000;

/**
 * Every state an appointment can be in. Deliberately closed rather than open-but-checked text (the
 * way `patients.source` or `vitals.vitalType` are) — a status drives the conflict-detection query
 * (`service.ts`'s "active" predicate) and the transition table below, both of which need a fixed,
 * exhaustively-handled set rather than an extensible one.
 */
export const APPOINTMENT_STATUSES = [
  'scheduled',
  'checked_in',
  'completed',
  'cancelled',
  'no_show',
] as const;
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number];

export function isAppointmentStatus(value: string): value is AppointmentStatus {
  return (APPOINTMENT_STATUSES as readonly string[]).includes(value);
}

/**
 * Legal next states for each status. `completed`, `cancelled` and `no_show` are terminal — none of
 * the three is ever re-opened by this surface; a mis-recorded outcome is corrected by booking a new
 * appointment, not by mutating a closed one back to `scheduled`, which would silently rewrite what a
 * calendar view already showed a front-desk user.
 *
 * A status "transitioning" to itself is deliberately not legal (`from === to` is checked first in
 * `canTransitionAppointmentStatus` and always denied) — the status-update route is for a state
 * change, and a same-state request is either a stale client retrying or a bug, neither of which
 * should silently succeed as a no-op.
 */
const ALLOWED_STATUS_TRANSITIONS: Record<AppointmentStatus, readonly AppointmentStatus[]> = {
  scheduled: ['checked_in', 'cancelled', 'no_show'],
  checked_in: ['completed', 'cancelled'],
  completed: [],
  cancelled: [],
  no_show: [],
};

/** May an appointment move from `from` to `to`? Pure, and the only place this rule is decided. */
export function canTransitionAppointmentStatus(
  from: AppointmentStatus,
  to: AppointmentStatus,
): boolean {
  if (from === to) return false;
  return ALLOWED_STATUS_TRANSITIONS[from].includes(to);
}

/** ISO 8601, UTC only (`Z`) — matching `columns.ts`'s `instant()`, which is always stored and
 * compared in UTC. A caller sending an offset instead of `Z` is refused at the boundary rather than
 * silently accepted and converted, so "what timezone did this arrive in" is never a guess. */
const instantSchema = z.iso.datetime();

const appointmentTypeSchema = z
  .string()
  .trim()
  .min(MIN_APPOINTMENT_TYPE_LENGTH, 'Enter an appointment type')
  .max(MAX_APPOINTMENT_TYPE_LENGTH, `Use at most ${MAX_APPOINTMENT_TYPE_LENGTH} characters`);

const notesSchema = z.string().trim().max(MAX_APPOINTMENT_NOTES_LENGTH);

const confidentialityReasonsSchema = z.array(z.enum(RESTRICTION_REASONS));

/** Later than `startsAt`, checked as instants rather than as strings — two UTC `Z` timestamps still
 * cannot be compared lexically once one has fractional seconds and the other does not.
 *
 * Exported so a client-side form (the web app's `AppointmentFormDialog`) reuses this exact
 * comparison instead of re-deriving it inline — `claude-kit/rules/working-style.md`'s "business
 * rules live in pure, tested modules, never inline in a component" applies to a two-line comparison
 * the same as it does to a larger rule; two independent implementations of "is B after A" is exactly
 * the kind of drift that rule exists to prevent, and this one already had a test suite here to reuse
 * rather than write a second one client-side. Takes two request-boundary strings — `startsAt`/`endsAt`
 * on the way *in* — not a server-echoed value, which may not be `Z`-suffixed (see `Appointment`'s own
 * comment on why its fields are plain `z.string()`); a caller with a server-echoed pair should compare
 * `Date` objects directly rather than assume this parses them the same way. */
export function endsAfterStarts(startsAt: string, endsAt: string): boolean {
  return new Date(endsAt).getTime() > new Date(startsAt).getTime();
}

/**
 * Book an appointment. No `status` field — every appointment starts `scheduled`, decided server-side,
 * matching `signupRequestSchema`'s "no `role` field" reasoning: a status accepted from the request
 * body is a way to create an appointment that claims to already be `completed`, with no audit trail
 * for whatever the create bypassed.
 */
export const createAppointmentRequestSchema = z
  .strictObject({
    patientId: z.uuid(),
    providerId: z.uuid(),
    appointmentType: appointmentTypeSchema,
    startsAt: instantSchema,
    endsAt: instantSchema,
    notes: notesSchema.optional(),
    confidentialityReasons: confidentialityReasonsSchema.optional(),
  })
  .refine((data) => endsAfterStarts(data.startsAt, data.endsAt), {
    error: 'endsAt must be after startsAt',
    path: ['endsAt'],
  });
export type CreateAppointmentRequest = z.infer<typeof createAppointmentRequestSchema>;

/**
 * Reschedule, move to another provider, or edit the type/notes. `patientId` is deliberately absent —
 * an appointment does not change whose it is; moving it to a different patient is a cancellation and
 * a new booking, not an edit, so that history stays honest about which patient which slot belonged
 * to.
 *
 * `notes` is nullable-and-optional on purpose, and the two are not the same thing here: omitting it
 * leaves the stored value unchanged, sending `null` clears it. Collapsing those would make "clear the
 * notes" inexpressible.
 *
 * At least one field must be present — an empty patch is refused rather than silently accepted as a
 * no-op update, the same reasoning `canTransitionAppointmentStatus` above applies to a same-state
 * transition.
 */
export const updateAppointmentRequestSchema = z
  .strictObject({
    providerId: z.uuid().optional(),
    appointmentType: appointmentTypeSchema.optional(),
    startsAt: instantSchema.optional(),
    endsAt: instantSchema.optional(),
    notes: notesSchema.nullable().optional(),
    confidentialityReasons: confidentialityReasonsSchema.optional(),
  })
  .refine((data) => Object.keys(data).length > 0, { error: 'at least one field must change' })
  .refine(
    (data) =>
      data.startsAt === undefined || data.endsAt === undefined
        ? true
        : endsAfterStarts(data.startsAt, data.endsAt),
    { error: 'endsAt must be after startsAt', path: ['endsAt'] },
  );
export type UpdateAppointmentRequest = z.infer<typeof updateAppointmentRequestSchema>;

/** A status transition. Nothing else changes in the same call — see this file's own header. */
export const updateAppointmentStatusRequestSchema = z.strictObject({
  status: z.enum(APPOINTMENT_STATUSES),
});
export type UpdateAppointmentStatusRequest = z.infer<typeof updateAppointmentStatusRequestSchema>;

/**
 * What a calendar view actually queries: one provider's schedule across a time range, or — when
 * `providerId` is omitted — every provider's schedule in the caller's own org across that range (the
 * "everyone's schedule" view, `BACKLOG.md` K2's follow-up). The single-provider shape still matches
 * `db/schema/scheduling.ts`'s own `appointments_org_provider_starts_idx`, built for exactly that case;
 * the org-wide case has no capability question to relax — `schedule:manage` is already an org-wide
 * capability every clinical role holds (`authorization.ts`'s `CAPABILITIES_BY_ROLE`) — only a query
 * shape to widen, which `server/src/scheduling/service.ts`'s `listAppointments` does as one query, not
 * N single-provider queries. Both `from`/`to` bounds stay required either way, so a client cannot
 * accidentally ask for a provider's — or an org's — entire history in one call.
 */
export const listAppointmentsQuerySchema = z
  .strictObject({
    providerId: z.uuid().optional(),
    from: instantSchema,
    to: instantSchema,
  })
  .refine((data) => endsAfterStarts(data.from, data.to), {
    error: 'to must be after from',
    path: ['to'],
  });
export type ListAppointmentsQuery = z.infer<typeof listAppointmentsQuerySchema>;

/** Path parameter for `/api/appointments/:id`. */
export const appointmentIdParamSchema = z.strictObject({ id: z.uuid() });

/**
 * One appointment, as returned to a client. `startsAt`/`endsAt`/`createdAt`/`updatedAt` are `z.string()`
 * rather than a strict ISO datetime check — they are round-tripping whatever Postgres's `timestamptz`
 * driver returned, which is not guaranteed to be the exact `Z`-suffixed shape the request schemas
 * above require on the way in.
 */
export const appointmentSchema = z.strictObject({
  id: z.uuid(),
  orgId: z.uuid(),
  patientId: z.uuid(),
  providerId: z.uuid(),
  appointmentType: z.string(),
  status: z.enum(APPOINTMENT_STATUSES),
  startsAt: z.string(),
  endsAt: z.string(),
  notes: z.string().nullable(),
  confidentialityReasons: z.array(z.enum(RESTRICTION_REASONS)),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Appointment = z.infer<typeof appointmentSchema>;

export const appointmentResponseSchema = z.strictObject({ appointment: appointmentSchema });
export type AppointmentResponse = z.infer<typeof appointmentResponseSchema>;

export const appointmentListResponseSchema = z.strictObject({
  appointments: z.array(appointmentSchema),
});
export type AppointmentListResponse = z.infer<typeof appointmentListResponseSchema>;

/** The failure vocabulary of this surface. Closed, matching `authRequests.ts`'s `AuthErrorCode`. */
export const SCHEDULING_ERROR_CODES = [
  /** The body or query failed schema validation. */
  'invalid_request',
  /** `patientId` does not resolve to a patient in the caller's own practice. */
  'patient_not_found',
  /** `providerId` does not resolve to a current member of the caller's own practice. */
  'provider_not_found',
  /** The appointment id does not resolve to a row in the caller's own practice. */
  'not_found',
  /** The requested time range overlaps another active appointment for the same provider. */
  'conflict',
  /** The requested time range overlaps a period the provider has marked unavailable
   * (`BACKLOG.md` M3a item 3, `scheduleBlocks`) — a distinct code from `conflict` above so an error
   * message can say *why* the provider is unbookable rather than implying a double-booked patient. */
  'provider_unavailable',
  /** The requested status change is not a legal transition from the appointment's current status. */
  'invalid_status_transition',
  /** Same meaning as `authRequests.ts`'s code of the same name: a write that cannot be audited did
   * not happen. */
  'audit_unavailable',
] as const;
export type SchedulingErrorCode = (typeof SCHEDULING_ERROR_CODES)[number];

export function isSchedulingErrorCode(value: string): value is SchedulingErrorCode {
  return (SCHEDULING_ERROR_CODES as readonly string[]).includes(value);
}

export const schedulingErrorResponseSchema = z.strictObject({
  error: z.enum(SCHEDULING_ERROR_CODES),
});
export type SchedulingErrorResponse = z.infer<typeof schedulingErrorResponseSchema>;

// --- Waitlist (`BACKLOG.md` K3) -------------------------------------------------------------------
//
// A patient wanting an earlier slot than what was available at booking time. Additive to the
// appointment lifecycle above, not a replacement for any of it — `K1`/`K2` already cover
// `cancelled`/`no_show` on the appointment itself; this is a separate queue staff work from when a
// slot opens up, matched by hand (see `db/schema/scheduling.ts`'s `waitlistEntries` header for why
// there is no automatic-match mechanism or `appointmentId` link).

export const MAX_WAITLIST_NOTES_LENGTH = MAX_APPOINTMENT_NOTES_LENGTH;

/**
 * Every state a waitlist entry can be in. Closed, matching `AppointmentStatus`'s own reasoning —
 * `waiting`/`matched`/`expired` drive a query (the "who is waiting for this provider" panel) and a
 * transition table, both of which need an exhaustively-handled set.
 */
export const WAITLIST_STATUSES = ['waiting', 'matched', 'expired'] as const;
export type WaitlistStatus = (typeof WAITLIST_STATUSES)[number];

export function isWaitlistStatus(value: string): value is WaitlistStatus {
  return (WAITLIST_STATUSES as readonly string[]).includes(value);
}

/**
 * `waiting` moves to `matched` (staff rebooked the patient into an opened slot) or `expired` (the
 * patient no longer wants it, or the desired window passed); both are terminal, matching
 * `ALLOWED_STATUS_TRANSITIONS`'s own "no re-opening a closed record" reasoning above — a
 * mis-recorded outcome is corrected by adding a new waitlist entry, not by reviving this one.
 */
const ALLOWED_WAITLIST_STATUS_TRANSITIONS: Record<WaitlistStatus, readonly WaitlistStatus[]> = {
  waiting: ['matched', 'expired'],
  matched: [],
  expired: [],
};

/** May a waitlist entry move from `from` to `to`? Pure, and the only place this rule is decided. */
export function canTransitionWaitlistStatus(from: WaitlistStatus, to: WaitlistStatus): boolean {
  if (from === to) return false;
  return ALLOWED_WAITLIST_STATUS_TRANSITIONS[from].includes(to);
}

/**
 * Add a patient to the waitlist. No `status` field, same "server decides the starting state"
 * reasoning as `createAppointmentRequestSchema` — every entry starts `waiting`. `providerId` is
 * optional: a patient may want "the next opening with anyone", not a specific clinician, unlike
 * booking an actual appointment.
 */
export const createWaitlistEntryRequestSchema = z
  .strictObject({
    patientId: z.uuid(),
    providerId: z.uuid().optional(),
    desiredFrom: instantSchema,
    desiredTo: instantSchema,
    notes: z.string().trim().max(MAX_WAITLIST_NOTES_LENGTH).optional(),
  })
  .refine((data) => endsAfterStarts(data.desiredFrom, data.desiredTo), {
    error: 'desiredTo must be after desiredFrom',
    path: ['desiredTo'],
  });
export type CreateWaitlistEntryRequest = z.infer<typeof createWaitlistEntryRequestSchema>;

/** A status transition on a waitlist entry — `matched` or `expired`, the only two legal targets from
 * `waiting`. Nothing else changes in the same call, matching `updateAppointmentStatusRequestSchema`. */
export const updateWaitlistEntryStatusRequestSchema = z.strictObject({
  status: z.enum(WAITLIST_STATUSES),
});
export type UpdateWaitlistEntryStatusRequest = z.infer<
  typeof updateWaitlistEntryStatusRequestSchema
>;

/** What the waitlist panel actually queries: every entry in the caller's own org, optionally
 * narrowed to one provider or one status (typically `waiting`, to see who is still owed a call). */
export const listWaitlistQuerySchema = z.strictObject({
  providerId: z.uuid().optional(),
  status: z.enum(WAITLIST_STATUSES).optional(),
});
export type ListWaitlistQuery = z.infer<typeof listWaitlistQuerySchema>;

/** Path parameter for `/api/waitlist-entries/:id`. */
export const waitlistEntryIdParamSchema = z.strictObject({ id: z.uuid() });

/** One waitlist entry, as returned to a client. Same `z.string()`-for-timestamps reasoning as
 * `appointmentSchema` above — these are round-tripping whatever Postgres's driver returned. */
export const waitlistEntrySchema = z.strictObject({
  id: z.uuid(),
  orgId: z.uuid(),
  patientId: z.uuid(),
  providerId: z.uuid().nullable(),
  desiredFrom: z.string(),
  desiredTo: z.string(),
  notes: z.string().nullable(),
  status: z.enum(WAITLIST_STATUSES),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type WaitlistEntry = z.infer<typeof waitlistEntrySchema>;

export const waitlistEntryResponseSchema = z.strictObject({ waitlistEntry: waitlistEntrySchema });
export type WaitlistEntryResponse = z.infer<typeof waitlistEntryResponseSchema>;

export const waitlistEntryListResponseSchema = z.strictObject({
  waitlistEntries: z.array(waitlistEntrySchema),
});
export type WaitlistEntryListResponse = z.infer<typeof waitlistEntryListResponseSchema>;

// --- No-show / cancellation reporting (`BACKLOG.md` K3) -------------------------------------------
//
// Structured visibility on top of the status data `K1`/`K2` already record — a rate a practice can
// actually review, not just a per-appointment flag. Reuses `listAppointmentsQuery`'s own
// `providerId`/`from`/`to` shape rather than inventing a parallel one.

/** How many of the most recent cancelled/no-show appointments the summary returns alongside the
 * counts — enough for a front-desk "who do I need to call back" list without returning the whole
 * range's history on every load. */
export const MAX_RECENT_SCHEDULING_EVENTS = 20;

export const noShowCancellationSummaryQuerySchema = z
  .strictObject({
    providerId: z.uuid().optional(),
    from: instantSchema,
    to: instantSchema,
  })
  .refine((data) => endsAfterStarts(data.from, data.to), {
    error: 'to must be after from',
    path: ['to'],
  });
export type NoShowCancellationSummaryQuery = z.infer<typeof noShowCancellationSummaryQuerySchema>;

/**
 * A rate is `null`, not `0`, when `totalAppointments` is zero — dividing by zero and calling the
 * result "0% no-show rate" would misrepresent an empty range as a good one; the UI renders `null` as
 * "no appointments in this range" rather than a rate at all.
 */
export const noShowCancellationSummarySchema = z.strictObject({
  from: z.string(),
  to: z.string(),
  totalAppointments: z.number().int().nonnegative(),
  noShowCount: z.number().int().nonnegative(),
  cancelledCount: z.number().int().nonnegative(),
  noShowRate: z.number().min(0).max(1).nullable(),
  cancellationRate: z.number().min(0).max(1).nullable(),
  recentNoShows: z.array(appointmentSchema),
  recentCancellations: z.array(appointmentSchema),
});
export type NoShowCancellationSummary = z.infer<typeof noShowCancellationSummarySchema>;

export const noShowCancellationSummaryResponseSchema = z.strictObject({
  summary: noShowCancellationSummarySchema,
});
export type NoShowCancellationSummaryResponse = z.infer<
  typeof noShowCancellationSummaryResponseSchema
>;

// --- Schedule blocks & derived availability (`BACKLOG.md` M3a item 3) -----------------------------
//
// Ported from `emrx`'s `Schedule`/`Slot` modeling (`docs/comparison-emrx.md` §5.1, `NOTES.md:1350-
// 1356`): "Unbookable time is modelled as `Slot` with `status: 'busy-unavailable'` against a
// per-provider `Schedule`... Only the *non*-bookable windows are materialised; free time is derived,
// because emitting every free 15-minute window per provider would be thousands of resources
// describing the absence of anything." `scheduleBlocks` below is that half — a real, persisted
// record of time a provider is *not* bookable (lunch, vacation, admin time), the thing this
// codebase had no way to represent at all before this (`hasOverlap` in `service.ts` only checked a
// booking against other *appointments*, so nothing stopped double-booking a provider's lunch break).
//
// `deriveAvailableRanges` below is the other half: a **pure function** that computes open time
// ranges for a window by subtracting booked appointments and blocked time from it — never persisted,
// never materialized as one row per candidate slot. It returns contiguous open ranges (one entry for
// a two-hour opening, not eight 15-minute rows), which is the specific anti-pattern this item exists
// to avoid.

/** Why a provider is unbookable for a period. Closed, matching `AppointmentStatus`'s own reasoning —
 * a small, fixed set a calendar UI can render distinctly (an icon, a color) rather than free text. */
export const SCHEDULE_BLOCK_REASONS = [
  'lunch',
  'vacation',
  'admin_time',
  'out_of_office',
  'other',
] as const;
export type ScheduleBlockReason = (typeof SCHEDULE_BLOCK_REASONS)[number];

export function isScheduleBlockReason(value: string): value is ScheduleBlockReason {
  return (SCHEDULE_BLOCK_REASONS as readonly string[]).includes(value);
}

export const MAX_SCHEDULE_BLOCK_NOTES_LENGTH = MAX_APPOINTMENT_NOTES_LENGTH;

/** Mark a period a provider is not bookable. `providerId` is always required, unlike
 * `waitlistEntries.providerId` — a block is inherently about one specific provider's own calendar,
 * never "anyone's". */
export const createScheduleBlockRequestSchema = z
  .strictObject({
    providerId: z.uuid(),
    startsAt: instantSchema,
    endsAt: instantSchema,
    reason: z.enum(SCHEDULE_BLOCK_REASONS),
    notes: z.string().trim().max(MAX_SCHEDULE_BLOCK_NOTES_LENGTH).optional(),
  })
  .refine((data) => endsAfterStarts(data.startsAt, data.endsAt), {
    error: 'endsAt must be after startsAt',
    path: ['endsAt'],
  });
export type CreateScheduleBlockRequest = z.infer<typeof createScheduleBlockRequestSchema>;

/** What the calendar view queries: one provider's blocked time across a range, or — when
 * `providerId` is omitted — every provider's in the caller's own org, matching
 * `listAppointmentsQuerySchema`'s own shape exactly. */
export const listScheduleBlocksQuerySchema = z
  .strictObject({
    providerId: z.uuid().optional(),
    from: instantSchema,
    to: instantSchema,
  })
  .refine((data) => endsAfterStarts(data.from, data.to), {
    error: 'to must be after from',
    path: ['to'],
  });
export type ListScheduleBlocksQuery = z.infer<typeof listScheduleBlocksQuerySchema>;

/** Path parameter for `/api/schedule-blocks/:id`. */
export const scheduleBlockIdParamSchema = z.strictObject({ id: z.uuid() });

/** One schedule block, as returned to a client. Same `z.string()`-for-timestamps reasoning as
 * `appointmentSchema` above. */
export const scheduleBlockSchema = z.strictObject({
  id: z.uuid(),
  orgId: z.uuid(),
  providerId: z.uuid(),
  startsAt: z.string(),
  endsAt: z.string(),
  reason: z.enum(SCHEDULE_BLOCK_REASONS),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ScheduleBlock = z.infer<typeof scheduleBlockSchema>;

export const scheduleBlockResponseSchema = z.strictObject({ scheduleBlock: scheduleBlockSchema });
export type ScheduleBlockResponse = z.infer<typeof scheduleBlockResponseSchema>;

export const scheduleBlockListResponseSchema = z.strictObject({
  scheduleBlocks: z.array(scheduleBlockSchema),
});
export type ScheduleBlockListResponse = z.infer<typeof scheduleBlockListResponseSchema>;

/** A half-open time interval, `[startsAt, endsAt)` — the shape both a booked appointment and a
 * blocked-time period reduce to for `deriveAvailableRanges` below. */
export interface TimeRange {
  readonly startsAt: string;
  readonly endsAt: string;
}

/**
 * Derive the bookable open ranges within `window` for one provider, given their already-booked
 * appointments and already-blocked time — **never** materializes a row per candidate slot; see this
 * section's own header. A caller wanting fixed-length slots (a 15-minute grid, say) chunks these
 * ranges client-side; that chunking is a display concern, not something this function should decide,
 * matching `emrx`'s own "free time is derived" reasoning: the source of truth is the busy intervals,
 * not any particular granularity of what is not busy.
 *
 * Implementation: clip every busy interval (booked + blocked, deliberately not distinguished from
 * here on — an available range must avoid both) to `window`, merge the ones that overlap or touch,
 * then return the gaps between what is left. `O(n log n)` in the number of busy intervals, via one
 * sort; a provider's real daily interval count is small enough that this is not worth a fancier
 * structure.
 */
export function deriveAvailableRanges(
  window: TimeRange,
  bookedAppointments: readonly TimeRange[],
  blockedTime: readonly TimeRange[],
): TimeRange[] {
  const windowStart = new Date(window.startsAt).getTime();
  const windowEnd = new Date(window.endsAt).getTime();
  if (!(windowEnd > windowStart)) return [];

  const clipped = [...bookedAppointments, ...blockedTime]
    .map((range) => ({
      start: new Date(range.startsAt).getTime(),
      end: new Date(range.endsAt).getTime(),
    }))
    // Only an interval that actually overlaps the window matters — one entirely before or after it
    // contributes nothing to what is busy *within* the window being asked about.
    .filter((interval) => interval.end > windowStart && interval.start < windowEnd)
    .map((interval) => ({
      start: Math.max(interval.start, windowStart),
      end: Math.min(interval.end, windowEnd),
    }))
    .sort((a, b) => a.start - b.start);

  const merged: { start: number; end: number }[] = [];
  for (const interval of clipped) {
    const last = merged.at(-1);
    if (last !== undefined && interval.start <= last.end) {
      last.end = Math.max(last.end, interval.end);
    } else {
      merged.push({ ...interval });
    }
  }

  const available: TimeRange[] = [];
  let cursor = windowStart;
  for (const interval of merged) {
    if (interval.start > cursor) {
      available.push({
        startsAt: new Date(cursor).toISOString(),
        endsAt: new Date(interval.start).toISOString(),
      });
    }
    cursor = Math.max(cursor, interval.end);
  }
  if (cursor < windowEnd) {
    available.push({
      startsAt: new Date(cursor).toISOString(),
      endsAt: new Date(windowEnd).toISOString(),
    });
  }
  return available;
}

// --- Patient-portal self-scheduling (`BACKLOG.md` W2 scheduling pillar, `docs/decisions/
// patient-portal-scope.md`'s "Scheduling" section) -------------------------------------------------
//
// The patient-facing consumer of `deriveAvailableRanges` above, and of the same underlying write
// `createAppointmentRequestSchema` already validates — `server/src/patientScheduling/service.ts`
// reuses `scheduling/service.ts`'s `createAppointment` directly rather than reimplementing the
// existence checks, conflict/block detection, and audit-savepoint write (see that file's own
// header). Two new request shapes, not a reuse of the staff ones above, because the patient surface
// genuinely differs on what a caller may supply: `patientId` never appears here — it is always the
// authenticated patient's own session identity, never a request value
// (`claude-kit/rules/invariants.md`'s "server-authoritative values never come from the request") —
// and `confidentialityReasons` is never patient-settable, an administrative/staff-only label this
// decision doc names no patient-facing use for.

/** What a patient's self-scheduling calendar actually queries: one provider's open time across a
 * bounded window. Unlike `listAppointmentsQuerySchema`, `providerId` is required, not optional — a
 * patient picks one provider to check before booking; there is no "everyone's schedule" view here
 * (that is a front-desk calendar concern, `docs/decisions/patient-portal-scope.md` names no
 * patient-facing use for it). */
export const availabilityQuerySchema = z
  .strictObject({
    providerId: z.uuid(),
    from: instantSchema,
    to: instantSchema,
  })
  .refine((data) => endsAfterStarts(data.from, data.to), {
    error: 'to must be after from',
    path: ['to'],
  });
export type AvailabilityQuery = z.infer<typeof availabilityQuerySchema>;

/** One open range, as returned to a client — the wire shape of `TimeRange` above (which is not
 * itself a Zod schema, only a `deriveAvailableRanges` parameter/return interface). */
export const timeRangeSchema = z.strictObject({ startsAt: z.string(), endsAt: z.string() });

export const availabilityResponseSchema = z.strictObject({ ranges: z.array(timeRangeSchema) });
export type AvailabilityResponse = z.infer<typeof availabilityResponseSchema>;

/**
 * Book an appointment for oneself. Same shape as `createAppointmentRequestSchema` minus `patientId`
 * (always the session's own identity) and `confidentialityReasons` (staff-only). Every appointment
 * still starts `scheduled`, decided server-side, matching that schema's own "no `status` field"
 * reasoning.
 */
export const patientPortalCreateAppointmentRequestSchema = z
  .strictObject({
    providerId: z.uuid(),
    appointmentType: appointmentTypeSchema,
    startsAt: instantSchema,
    endsAt: instantSchema,
    notes: notesSchema.optional(),
  })
  .refine((data) => endsAfterStarts(data.startsAt, data.endsAt), {
    error: 'endsAt must be after startsAt',
    path: ['endsAt'],
  });
export type PatientPortalCreateAppointmentRequest = z.infer<
  typeof patientPortalCreateAppointmentRequestSchema
>;
